// Curated Location Gazetteer (Option A)
// - Countries (subset)
// - US States
// - Top global cities (subset; expandable)
// Includes aliases to match colloquial text (e.g., "SF", "Bay Area")

export interface LocationEntry {
  name: string;            // Display name, e.g., "San Francisco"
  slug: string;            // Normalized community name, e.g., "sanfrancisco"
  aliases: string[];       // Lowercase aliases to match in text
  type: 'city' | 'state' | 'country';
}

const n = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export const COUNTRIES: LocationEntry[] = [
  { name: 'United States', slug: n('United States'), aliases: ['united states', 'usa', 'us', 'america', 'u.s.', 'u.s.a.'], type: 'country' },
  { name: 'Canada', slug: n('Canada'), aliases: ['canada', 'ca'], type: 'country' },
  { name: 'United Kingdom', slug: n('United Kingdom'), aliases: ['united kingdom', 'uk', 'u.k.', 'britain', 'great britain'], type: 'country' },
  { name: 'France', slug: n('France'), aliases: ['france', 'fr'], type: 'country' },
  { name: 'Germany', slug: n('Germany'), aliases: ['germany', 'de', 'deutschland'], type: 'country' },
  { name: 'Japan', slug: n('Japan'), aliases: ['japan', 'jp', 'nihon', 'nippon'], type: 'country' },
  { name: 'Australia', slug: n('Australia'), aliases: ['australia', 'au', 'aussie'], type: 'country' },
  { name: 'Brazil', slug: n('Brazil'), aliases: ['brazil', 'br', 'brasil'], type: 'country' },
  { name: 'India', slug: n('India'), aliases: ['india', 'in', 'bharat'], type: 'country' },
];

export const US_STATES: LocationEntry[] = [
  { name: 'California', slug: n('California'), aliases: ['california', 'ca', 'cali'], type: 'state' },
  { name: 'New York', slug: n('New York'), aliases: ['new york state', 'nys', 'ny'], type: 'state' },
  { name: 'Texas', slug: n('Texas'), aliases: ['texas', 'tx'], type: 'state' },
  { name: 'Florida', slug: n('Florida'), aliases: ['florida', 'fl'], type: 'state' },
  { name: 'Washington', slug: n('Washington'), aliases: ['washington state', 'wa'], type: 'state' },
  { name: 'Illinois', slug: n('Illinois'), aliases: ['illinois', 'il'], type: 'state' },
  { name: 'Massachusetts', slug: n('Massachusetts'), aliases: ['massachusetts', 'ma'], type: 'state' },
  { name: 'Colorado', slug: n('Colorado'), aliases: ['colorado', 'co'], type: 'state' },
  { name: 'Nevada', slug: n('Nevada'), aliases: ['nevada', 'nv'], type: 'state' },
  { name: 'Arizona', slug: n('Arizona'), aliases: ['arizona', 'az'], type: 'state' },
];

export const TOP_CITIES: LocationEntry[] = [
  // US
  { name: 'San Francisco', slug: n('San Francisco'), aliases: ['san francisco', 'sf', 'bay area', 'san fran'], type: 'city' },
  { name: 'Los Angeles', slug: n('Los Angeles'), aliases: ['los angeles', 'la', 'l.a.'], type: 'city' },
  { name: 'New York City', slug: n('New York City'), aliases: ['nyc', 'new york city', 'new york', 'ny'], type: 'city' },
  { name: 'Seattle', slug: n('Seattle'), aliases: ['seattle'], type: 'city' },
  { name: 'Austin', slug: n('Austin'), aliases: ['austin', 'atx'], type: 'city' },
  { name: 'Chicago', slug: n('Chicago'), aliases: ['chicago', 'chi'], type: 'city' },
  { name: 'Miami', slug: n('Miami'), aliases: ['miami'], type: 'city' },
  { name: 'Las Vegas', slug: n('Las Vegas'), aliases: ['las vegas', 'vegas', 'lv'], type: 'city' },
  { name: 'San Diego', slug: n('San Diego'), aliases: ['san diego', 'sd'], type: 'city' },
  // Europe
  { name: 'London', slug: n('London'), aliases: ['london', 'ldn'], type: 'city' },
  { name: 'Paris', slug: n('Paris'), aliases: ['paris'], type: 'city' },
  { name: 'Berlin', slug: n('Berlin'), aliases: ['berlin'], type: 'city' },
  { name: 'Barcelona', slug: n('Barcelona'), aliases: ['barcelona', 'bcn'], type: 'city' },
  { name: 'Amsterdam', slug: n('Amsterdam'), aliases: ['amsterdam', 'ams'], type: 'city' },
  // Asia
  { name: 'Tokyo', slug: n('Tokyo'), aliases: ['tokyo'], type: 'city' },
  { name: 'Seoul', slug: n('Seoul'), aliases: ['seoul'], type: 'city' },
  { name: 'Singapore', slug: n('Singapore'), aliases: ['singapore', 'sg'], type: 'city' },
  { name: 'Hong Kong', slug: n('Hong Kong'), aliases: ['hong kong', 'hk'], type: 'city' },
  { name: 'Bangkok', slug: n('Bangkok'), aliases: ['bangkok', 'bkk'], type: 'city' },
  // LATAM
  { name: 'Mexico City', slug: n('Mexico City'), aliases: ['mexico city', 'cdmx', 'mexico d.f.', 'df'], type: 'city' },
  { name: 'São Paulo', slug: n('São Paulo'), aliases: ['sao paulo', 'são paulo', 'sp'], type: 'city' },
  { name: 'Buenos Aires', slug: n('Buenos Aires'), aliases: ['buenos aires', 'ba'], type: 'city' },
  // Oceania
  { name: 'Sydney', slug: n('Sydney'), aliases: ['sydney', 'syd'], type: 'city' },
  { name: 'Melbourne', slug: n('Melbourne'), aliases: ['melbourne', 'mel'], type: 'city' },
];

export const ALL_LOCATIONS: LocationEntry[] = [
  ...COUNTRIES,
  ...US_STATES,
  ...TOP_CITIES,
];

export function searchLocations(partial: string, limit = 6): LocationEntry[] {
  const q = partial.trim().toLowerCase();
  if (!q) return ALL_LOCATIONS.slice(0, limit);

  const matches = ALL_LOCATIONS.filter(loc =>
    loc.aliases.some(a => a.startsWith(q)) || loc.name.toLowerCase().startsWith(q)
  );

  // Bias by type (countries -> states -> cities) can be tuned; for now,
  // show cities first for user intent, then states, then countries.
  matches.sort((a, b) => {
    const rank = (t: LocationEntry['type']) => (t === 'city' ? 3 : t === 'state' ? 2 : 1);
    const r = rank(b.type) - rank(a.type);
    return r !== 0 ? r : a.name.localeCompare(b.name);
  });

  return matches.slice(0, limit);
}

export function detectLocationsInText(text: string): LocationEntry[] {
  const lower = text.toLowerCase();
  // Replace common punctuation with spaces for easier matching
  const normalized = lower.replace(/[.,!?;:()\[\]{}"'`]/g, ' ');

  const results: LocationEntry[] = [];
  const seen = new Set<string>();

  const mkPattern = (phrase: string) => {
    // Escape regex specials, convert spaces to flexible whitespace
    const escaped = phrase.trim().replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&').replace(/\s+/g, '\\s+');
    return new RegExp(`(^|\n|\r|\t|\s)${escaped}(?=$|\n|\r|\t|\s)`, 'i');
  };

  for (const loc of ALL_LOCATIONS) {
    if (seen.has(loc.slug)) continue;
    const namePattern = mkPattern(loc.name.toLowerCase());
    let hit = namePattern.test(normalized);
    if (!hit) {
      for (const a of loc.aliases) {
        const ap = mkPattern(a);
        if (ap.test(normalized)) { hit = true; break; }
      }
    }
    if (hit) {
      results.push(loc);
      seen.add(loc.slug);
      if (results.length > 10) break; // safety cap
    }
  }
  return results;
}
